/**
 * Study Library Component
 * 
 * Main container for the redesigned Results/Library page.
 * Provides hierarchical organization of studies by symbol/timeframe
 * with filtering, staleness indicators, and comparison features.
 */

import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertCircle,
  Box,
  GitCompare,
  Library,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { useApi } from "@/hooks/useApi";
import type { Study } from "@/types/api";
import { logger } from "@/utils/logger";
import {
  type FreshnessStatus,
  type GroupedStudies,
  type StudyFilters,
  filterGroupedStudies,
  getDistinctLabels,
  groupStudiesBySymbolAndTimeframe,
} from "@/utils/studyGrouping";

import { StudyFilters as StudyFiltersPanel } from "./StudyFilters";
import { StudyGroupList } from "./StudyGroupList";
import { ComparisonPanel } from "./ComparisonPanel";
import { useStudyComparison } from "./hooks/useStudyComparison";

// =============================================================================
// Constants
// =============================================================================

const STUDIES_FETCH_PAGE_SIZE = 100;
const STUDIES_FETCH_MAX_PAGES = 5;

// =============================================================================
// Main Component
// =============================================================================

export function StudyLibrary() {
  const api = useApi();
  const navigate = useNavigate();
  
  // Filter state
  const [filters, setFilters] = useState<StudyFilters>({
    symbols: [],
    timeframes: [],
    freshness: [],
    searchQuery: "",
    parallelMode: "all",
    showLabeledOnly: false,
    selectedLabels: [],
  });
  
  // Comparison state
  const {
    selectedStudies,
    isComparing,
    toggleStudySelection,
    selectMultipleStudies,
    clearSelection,
    openComparison,
    closeComparison,
    canCompare,
  } = useStudyComparison();
  
  // Fetch studies
  const {
    data: studiesData,
    error: studiesError,
    isLoading: studiesLoading,
    refetch: refetchStudies,
  } = useQuery({
    queryKey: ["studies", "library"],
    queryFn: async () => {
      logger.info("StudyLibrary: Fetching studies");
      const aggregatedStudies: Study[] = [];
      let total = 0;

      for (let page = 0; page < STUDIES_FETCH_MAX_PAGES; page += 1) {
        const offset = page * STUDIES_FETCH_PAGE_SIZE;
        const pageResult = await api.listStudies(offset, STUDIES_FETCH_PAGE_SIZE);

        aggregatedStudies.push(...(pageResult.studies ?? []));
        const pageTotal = pageResult.total ?? 0;
        if (pageTotal > 0) {
          total = pageTotal;
        } else {
          total = Math.max(total, aggregatedStudies.length);
        }

        const fetchedLessThanPage =
          (pageResult.studies?.length ?? 0) < STUDIES_FETCH_PAGE_SIZE;
        const reachedTotal =
          pageTotal > 0 && offset + STUDIES_FETCH_PAGE_SIZE >= pageTotal;

        if (fetchedLessThanPage || reachedTotal) {
          break;
        }
      }

      return {
        studies: aggregatedStudies,
        total: total || aggregatedStudies.length,
      };
    },
    retry: 3,
  });

  // Filter to completed studies only and group them
  const groupedStudies = useMemo<GroupedStudies | null>(() => {
    if (!studiesData?.studies) return null;

    try {
      const completedStudies = studiesData.studies.filter(
        (s: Study) => s.status === "completed"
      );

      logger.info("StudyLibrary: Grouping studies", {
        total: studiesData.studies.length,
        completed: completedStudies.length,
      });

      return groupStudiesBySymbolAndTimeframe(completedStudies);
    } catch (error) {
      logger.error("StudyLibrary: Failed to group studies", error);
      return null;
    }
  }, [studiesData?.studies]);

  // AIE-539: distinct labels (with color + count) for the per-label filter checklist.
  const allLabels = useMemo(() => {
    const completedStudies = (studiesData?.studies ?? []).filter(
      (s: Study) => s.status === "completed"
    );
    return getDistinctLabels(completedStudies);
  }, [studiesData?.studies]);

  // Apply filters
  const filteredGroups = useMemo<GroupedStudies | null>(() => {
    if (!groupedStudies) return null;

    const hasFilters =
      filters.symbols.length > 0 ||
      filters.timeframes.length > 0 ||
      filters.freshness.length > 0 ||
      filters.searchQuery.length > 0 ||
      filters.parallelMode !== "all" ||
      filters.showLabeledOnly ||
      filters.selectedLabels.length > 0;

    if (!hasFilters) return groupedStudies;
    
    return filterGroupedStudies(groupedStudies, filters);
  }, [groupedStudies, filters]);

  // Filter handlers
  const handleSymbolFilterChange = useCallback((symbols: string[]) => {
    setFilters((f) => ({ ...f, symbols }));
  }, []);

  const handleTimeframeFilterChange = useCallback((timeframes: string[]) => {
    setFilters((f) => ({ ...f, timeframes }));
  }, []);

  const handleFreshnessFilterChange = useCallback((freshness: FreshnessStatus[]) => {
    setFilters((f) => ({ ...f, freshness }));
  }, []);

  const handleSearchChange = useCallback((searchQuery: string) => {
    setFilters((f) => ({ ...f, searchQuery }));
  }, []);

  const handleParallelModeChange = useCallback((parallelMode: StudyFilters["parallelMode"]) => {
    setFilters((f) => ({ ...f, parallelMode }));
  }, []);

  const handleShowLabeledOnlyChange = useCallback((showLabeledOnly: boolean) => {
    setFilters((f) => ({ ...f, showLabeledOnly }));
  }, []);

  const handleLabelToggle = useCallback((label: string) => {
    setFilters((f) => ({
      ...f,
      selectedLabels: f.selectedLabels.includes(label)
        ? f.selectedLabels.filter((l) => l !== label)
        : [...f.selectedLabels, label],
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      symbols: [],
      timeframes: [],
      freshness: [],
      searchQuery: "",
      parallelMode: "all",
      showLabeledOnly: false,
      selectedLabels: [],
    });
  }, []);

  // Navigation handlers
  const handleStudyClick = useCallback(
    (studyId: string) => {
      navigate(`/results/${studyId}`);
    },
    [navigate]
  );

  const handleNewStudy = useCallback(() => {
    navigate("/new-study");
  }, [navigate]);

  // Check if any filters are active
  const hasActiveFilters =
    filters.symbols.length > 0 ||
    filters.timeframes.length > 0 ||
    filters.freshness.length > 0 ||
    filters.searchQuery.length > 0 ||
    filters.parallelMode !== "all" ||
    filters.showLabeledOnly ||
    filters.selectedLabels.length > 0;

  // Render loading state
  if (studiesLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Header
          selectedCount={selectedStudies.length}
          canCompare={canCompare}
          onCompare={openComparison}
          onNewStudy={handleNewStudy}
          on3DCompare={() => navigate("/results/compare")}
        />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
            <p className="text-sm text-dark-400">Loading your study library...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (studiesError) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Header
          selectedCount={selectedStudies.length}
          canCompare={canCompare}
          onCompare={openComparison}
          onNewStudy={handleNewStudy}
          on3DCompare={() => navigate("/results/compare")}
        />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-sm text-red-400 mb-4">Failed to load studies</p>
            <Button onClick={() => refetchStudies()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (!groupedStudies || groupedStudies.totalStudies === 0) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Header
          selectedCount={selectedStudies.length}
          canCompare={canCompare}
          onCompare={openComparison}
          onNewStudy={handleNewStudy}
          on3DCompare={() => navigate("/results/compare")}
        />
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <Library className="h-16 w-16 text-dark-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-dark-200 mb-2">
              Your study library is empty
            </h3>
            <p className="text-sm text-dark-400 mb-6">
              Complete your first optimization study to see it here. Studies help
              you track and compare different parameter configurations.
            </p>
            <Button onClick={handleNewStudy} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Your First Study
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Header
        selectedCount={selectedStudies.length}
        canCompare={canCompare}
        onCompare={openComparison}
        onNewStudy={handleNewStudy}
        on3DCompare={() => navigate("/results/compare")}
      />

      {/* Stats summary */}
      <div className="flex items-center gap-6 text-sm text-dark-400">
        <span>
          <span className="text-dark-200 font-medium">{groupedStudies.totalStudies}</span> Studies
        </span>
        <span className="text-green-400">
          {groupedStudies.freshCount} Fresh
        </span>
        <span className="text-amber-400">
          {groupedStudies.agingCount} Aging
        </span>
        <span className="text-red-400">
          {groupedStudies.staleCount} Stale
        </span>
      </div>

      {/* Main content */}
      <div className="flex gap-6">
        {/* Filters sidebar */}
        <div className="w-56 flex-shrink-0">
          <StudyFiltersPanel
            allSymbols={groupedStudies.allSymbols}
            allTimeframes={groupedStudies.allTimeframes}
            selectedSymbols={filters.symbols}
            selectedTimeframes={filters.timeframes}
            selectedFreshness={filters.freshness}
            selectedParallelMode={filters.parallelMode}
            searchQuery={filters.searchQuery}
            showLabeledOnly={filters.showLabeledOnly}
            allLabels={allLabels}
            selectedLabels={filters.selectedLabels}
            onSymbolChange={handleSymbolFilterChange}
            onTimeframeChange={handleTimeframeFilterChange}
            onFreshnessChange={handleFreshnessFilterChange}
            onParallelModeChange={handleParallelModeChange}
            onSearchChange={handleSearchChange}
            onShowLabeledOnlyChange={handleShowLabeledOnlyChange}
            onLabelToggle={handleLabelToggle}
            onClearAll={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>

        {/* Study groups */}
        <div className="flex-1 min-w-0">
          {filteredGroups && filteredGroups.totalStudies > 0 ? (
            <StudyGroupList
              groups={filteredGroups.groups}
              selectedStudyIds={selectedStudies.map((s) => s.study_id)}
              onStudyClick={handleStudyClick}
              onStudySelect={toggleStudySelection}
              onCompareAll={selectMultipleStudies}
            />
          ) : hasActiveFilters ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 text-dark-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-dark-200 mb-2">
                  No matching studies
                </h3>
                <p className="text-sm text-dark-400 mb-4">
                  Try adjusting your filters to see more results.
                </p>
                <Button onClick={handleClearFilters} variant="outline" size="sm">
                  Clear Filters
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Floating comparison bar */}
      <AnimatePresence>
        {selectedStudies.length > 0 && !isComparing && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="flex items-center gap-4 px-6 py-3 bg-dark-900/95 backdrop-blur-sm border border-dark-700 rounded-full shadow-xl">
              <span className="text-sm text-dark-300">
                <span className="text-blue-400 font-medium">
                  {selectedStudies.length}
                </span>{" "}
                {selectedStudies.length === 1 ? "study" : "studies"} selected
              </span>
              <div className="h-4 w-px bg-dark-700" />
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                className="text-dark-400 hover:text-dark-200"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
              <Button
                size="sm"
                onClick={openComparison}
                disabled={!canCompare}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <GitCompare className="h-4 w-4 mr-2" />
                Compare
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison panel */}
      <ComparisonPanel
        isOpen={isComparing}
        studies={selectedStudies}
        onClose={closeComparison}
        onStudyClick={handleStudyClick}
      />
    </div>
  );
}

// =============================================================================
// Header Component
// =============================================================================

interface HeaderProps {
  selectedCount: number;
  canCompare: boolean;
  onCompare: () => void;
  onNewStudy: () => void;
  on3DCompare: () => void;
}

function Header({ selectedCount, canCompare, onCompare, onNewStudy, on3DCompare }: HeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-brand-blue to-brand-cyan bg-clip-text text-transparent">
          Study Library
        </h2>
        <p className="text-muted-foreground">
          Organize, compare, and manage your optimization studies
        </p>
      </div>
      <div className="flex items-center gap-3">
        {selectedCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCompare}
            disabled={!canCompare}
            className="gap-2"
          >
            <GitCompare className="h-4 w-4" />
            Compare ({selectedCount})
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={on3DCompare}
          className="gap-2"
        >
          <Box className="h-4 w-4" />
          3D Compare
        </Button>
        <Button onClick={onNewStudy} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          New Study
        </Button>
      </div>
    </div>
  );
}

export default StudyLibrary;
